import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { EventEmitterService } from '../../../services/event-emitter.service';
import { debounceTime, fromEvent, Subject, switchMap, tap } from 'rxjs';
import { TeamMembersService } from '../../../services/team-members.service';
import { BranchesService } from '../../../services/branches.service';
import { OrdersService } from '../../../services/orders.service';
import { TranslateService } from '@ngx-translate/core';
import * as L from 'leaflet';
import { ThirdpartyService } from '../../../services/thirdparty.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { AccountTypes } from '../../../models/account_types';

@Component({
  standalone: false,
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrl: './analytics.component.scss',
})
export class AnalyticsComponent implements OnInit {
  user;
  @ViewChild('searchInput') searchInput: ElementRef;
  riders1 = [];
  search = '';
  searchRiders1 = new Subject<string>();
  searchRider1NoResults = 'no-results-found';
  selectedRider;
  branches = [];
  selectedBranch;
  searchBranches = new Subject<string>();
  searchBranchNoResults = 'no-results-found';
  thirdParties = [];
  selectedThirdParty = null;
  searchThirdParties = new Subject<string>();
  searchThirdPartiesNoResults = 'no-results-found';
  mymap: L.Map;
  locationLatitude;
  locationLongitude;
  isLoading = false;
  gridCellsPolygons: L.Polygon[] = [];
  mapMoveSubject = new Subject<void>();
  accountTypes = AccountTypes;

  // Date range filters
  startDate: Date;
  endDate: Date;

  // Analytics data properties
  totalOrders = 0;
  totalMoney = 0;
  topProducts = [];
  topCategories = [];
  gridCells = [];
  currentZoomLevel = 13;
  maxTotalSold = 0;

  constructor(
    public eventEmitterService: EventEmitterService,
    private teamMembersService: TeamMembersService,
    private thirdPartiesService: ThirdpartyService,
    private authenticationService: AuthenticationService,
    private branchesService: BranchesService,
    private ordersService: OrdersService,
    private translateService: TranslateService,
  ) {}

  async ngOnInit() {
    await this.getUser();
    this.setupDates();
    this.getAllRiders1('');
    this.getAllThirdParties('');
    this.getAllBranches('');
    this.setupMapMove();
  }

  ngAfterViewInit(): void {
    this.setupSearch();
    this.getUserLocation();
  }

  ngOnDestroy() {
    if (this.mymap) {
      this.clearGridCellsPolygons();
      this.mymap.off();
      this.mymap.remove();
    }
  }

  async getUser() {
    this.user = await this.authenticationService.getUserFromToken();
  }

  setupDates() {
    let endDate = new Date();
    let startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);
    this.startDate = this.formatDate(startDate);
    this.endDate = this.formatDate(endDate);
  }

  setupMapMove() {
    // Subscribe to map movements with debounce
    this.mapMoveSubject.pipe(debounceTime(3000)).subscribe(() => {
      this.getAnalytics();
    });
  }

  getAllRiders1(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.teamMembersService.getAllRiders(apiData).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        this.searchRider1NoResults = 'no-results-found';
        this.riders1 = response.items;
        this.riders1 = this.riders1.map((item) => ({
          ...item,
          fullName: item.rider.name,
        }));
        let noneString = this.translateService.instant('none');
        this.riders1.unshift({ id: 0, fullName: noneString });
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchRider1NoResults = 'no-results-found';
      },
    );
  }

  getAllThirdParties(search) {
    const apiData: any = {};
    if (search && search.trim().length > 0) {
      apiData.search = search;
    }
    this.thirdPartiesService.getAllThirdParties(apiData).subscribe(
      (response: any) => {
        console.log('response:');
        console.log(response);
        this.searchThirdPartiesNoResults = 'no-results-found';
        this.thirdParties = response.items;
      },
      (error) => {
        console.log('error:');
        console.log(error);
        this.searchThirdPartiesNoResults = 'no-results-found';
      },
    );
  }

  getAnalytics() {
    const apiData: any = {};

    if (this.selectedRider) {
      apiData.assigned_to = this.selectedRider.id;
    }
    if (this.selectedThirdParty) {
      apiData.third_party_id = this.selectedThirdParty.id;
    }
    if (this.selectedBranch) {
      apiData.branch_id = this.selectedBranch.id;
    }
    if (this.search && this.search.trim().length > 0) {
      apiData.search = this.search;
    }

    // Add date range parameters
    if (this.startDate) {
      apiData.start_date = new Date(this.startDate).toISOString();
    }
    if (this.endDate) {
      apiData.end_date = new Date(this.endDate).toISOString();
    }

    // Add map bounds if map is initialized
    if (this.mymap) {
      const bounds = this.mymap.getBounds();
      apiData.mapBounds = {
        northEast: {
          lat: bounds.getNorthEast().lat,
          lng: bounds.getNorthEast().lng,
        },
        southWest: {
          lat: bounds.getSouthWest().lat,
          lng: bounds.getSouthWest().lng,
        },
      };

      // Add current zoom level to the API request
      apiData.zoom_level = this.currentZoomLevel;
    }

    this.isLoading = true;
    this.ordersService.getAnalytics(apiData).subscribe(
      (response: any) => {
        this.isLoading = false;
        console.log('response:');
        console.log(response);

        // Update component properties with API response data
        this.totalOrders = response.totalOrders || 0;
        this.totalMoney = response.totalMoney || 0;
        this.topProducts = response.topProducts || [];
        this.topCategories = response.topCategories || [];
        this.gridCells = response.gridCells || [];
        this.maxTotalSold = response.maxTotalSold || 0;

        // Draw gridCell boundaries on the map
        if (this.mymap) {
          this.drawGridCellBoundaries();
        }
      },
      (error) => {
        this.isLoading = false;
        console.log('error:');
        console.log(error);
      },
    );
  }

  formatDate(date: Date): any {
    if (!date) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const hour = String(date.getHours()).padStart(2, '0');
    const minute = String(date.getMinutes()).padStart(2, '0');
    const second = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
  }

  // Date range changed
  onDateChange() {
    this.getAnalytics();
  }

  getAllBranches(search) {
    const apiData: any = {
      page: 1,
      pageSize: 10,
    };

    if (search && search.trim().length > 0) {
      apiData.search = this.search;
    }
    this.branchesService.getAllParentBranches(apiData).subscribe(
      (response: any) => {
        this.branches = response.items;
      },
      (error) => {
        console.log('error:');
        console.log(error);
      },
    );
  }

  setupSearch() {
    fromEvent(this.searchInput.nativeElement, 'input')
      .pipe(
        tap(() => (this.isLoading = true)),
        debounceTime(3000),
      )
      .subscribe((event: Event) => {
        this.getAnalytics();
      });

    this.searchRiders1
      .pipe(
        tap(() => (this.searchRider1NoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllRiders1(searchText)),
      )
      .subscribe();

    this.searchThirdParties
      .pipe(
        tap(() => (this.searchThirdPartiesNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllThirdParties(searchText)),
      )
      .subscribe();

    this.searchBranches
      .pipe(
        tap(() => (this.searchBranchNoResults = 'loading')),
        debounceTime(3000),
        switchMap(async (searchText) => this.getAllBranches(searchText)),
      )
      .subscribe();
  }

  setSearch(event: any) {
    this.search = event.target.value;
  }

  setAssignedTo(event: any) {
    this.selectedRider = event.value;
    this.getAnalytics();
  }

  setThirdParty(event) {
    this.selectedThirdParty = event.value;
    this.getAnalytics();
  }

  setBranch(event: any) {
    this.selectedBranch = event.value;
    this.getAnalytics();
  }

  async setupMap() {
    if (this.mymap) {
      this.mymap.remove();
    }

    this.mymap = L.map('map', {
      center: [this.locationLatitude, this.locationLongitude],
      zoom: 13,
      minZoom: 9,
      maxZoom: 15,
      renderer: L.canvas(),
    });

    // Initialize the current zoom level
    this.currentZoomLevel = this.mymap.getZoom();

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      className: 'map-tiles',
    }).addTo(this.mymap);

    this.mymap.on('click', (e) => {
      var latlng = e.latlng;

      this.locationLatitude = latlng.lat;
      this.locationLongitude = latlng.lng;
    });

    // Track zoom level changes
    this.mymap.on('zoomend', () => {
      this.currentZoomLevel = this.mymap.getZoom();
      console.log('Zoom level changed to:', this.currentZoomLevel);
      this.mapMoveSubject.next();
    });

    // Add event listener for when map view changes (zoom or pan)
    this.mymap.on('moveend', () => {
      this.mapMoveSubject.next();
    });

    setTimeout(() => {
      this.mymap.invalidateSize();
    }, 1000);

    // Initial analytics load with map bounds after map is ready
    setTimeout(() => {
      this.getAnalytics();
    }, 2000);
  }

  getUserLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position: GeolocationPosition) => {
          this.locationLatitude = position.coords.latitude;
          this.locationLongitude = position.coords.longitude;
          this.setupMap();
        },
        (error: GeolocationPositionError) => {
          console.log('error:');
          console.log(error);
          this.locationLatitude = 52.52;
          this.locationLongitude = 13.405;
          this.setupMap();
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
      this.locationLatitude = 52.52;
      this.locationLongitude = 13.405;
      this.setupMap();
    }
  }

  // Draw gridCell boundaries on the map
  drawGridCellBoundaries() {
    console.log('Drawing gridCell boundaries', this.gridCells);

    // Clear existing gridCell polygons
    this.clearGridCellsPolygons();

    // Process each gridCell
    this.gridCells.forEach((gridCell) => {
      // Skip gridCells without necessary coordinates or color
      if (
        !gridCell.southwest ||
        !gridCell.northeast ||
        !gridCell.topProductColor
      ) {
        return;
      }

      // Create polygon from southwest and northeast corners
      const sw = L.latLng(gridCell.southwest.lat, gridCell.southwest.lng);
      const ne = L.latLng(gridCell.northeast.lat, gridCell.northeast.lng);
      const se = L.latLng(gridCell.southwest.lat, gridCell.northeast.lng);
      const nw = L.latLng(gridCell.northeast.lat, gridCell.southwest.lng);

      // Create rectangle using the corners
      const latLngs = [sw, se, ne, nw];

      // Create polygon with the top product color
      const polygon = L.polygon(latLngs, {
        color: gridCell.topProductColor,
        fillColor: gridCell.topProductColor,
        fillOpacity: this.calculateOpacity(gridCell.totalSold),
        weight: 1.5,
        opacity: this.calculateOpacity(gridCell.totalSold, 0.5),
      });

      // Add popup with information about the gridCell
      polygon.bindPopup(`
        <div>
          <p>Top Product: ${gridCell.topProduct || 'None'}</p>
          <p>Total Sold: ${gridCell.totalSold || 0}</p>
        </div>
      `);

      // Add polygon to map
      polygon.addTo(this.mymap);

      // Store for later removal
      this.gridCellsPolygons.push(polygon);
    });
  }

  // Remove all gridCell polygons from the map
  clearGridCellsPolygons() {
    this.gridCellsPolygons.forEach((polygon) => {
      if (this.mymap) {
        this.mymap.removeLayer(polygon);
      }
    });
    this.gridCellsPolygons = [];
  }

  calculateOpacity(totalSold: number, maxOpacity = 0.8): number {
    if (!totalSold) return 0.1;

    // Calculate normalized opacity between 0.1 and maxOpacity
    // Higher sales = higher opacity
    const minOpacity = 0.1;
    const maxValue = this.maxTotalSold || 1; // Avoid division by zero

    return minOpacity + (totalSold / maxValue) * (maxOpacity - minOpacity);
  }
}
